import type { GetStaticProps, NextPage } from 'next'
import dynamic from 'next/dynamic'

import classNames from 'classnames'

import type { BreadcrumbProps } from '~/components/atoms/Breadcrumb/Breadcrumb'
import Breadcrumb from '~/components/atoms/Breadcrumb/Breadcrumb'
import type { ImageProps } from '~/components/atoms/Image/Image'
import Image from '~/components/atoms/Image/Image'
import Text, { TextSizes } from '~/components/atoms/Text/Text'
import FeaturedPartners from '~/components/directory/FeaturedPartners/FeaturedPartners'
import HeaderCTA from '~/components/directory/HeaderCTA/HeaderCTA'
import type {
  PartnerCardProps,
  PartnerCategories,
} from '~/components/directory/PartnerCard/PartnerCard'
import PartnerHeader from '~/components/directory/PartnerHeader/PartnerHeader'
import { resolveBlock } from '~/components/serializers'
import {
  createBaseSelector,
  detectBodyModuleTypes,
  getBodyModules,
  internationalizedField,
  myPortfolioBanner,
  partnerCard,
} from '~/schemas/common'
import { getFAQData } from '~/schemas/JsonLd'
import { useSanity } from '~/services/sanity'
import { buildBreadcrumb, getBreadcrumbJsonLd } from '~/utils/breadcrumb'
import { addTranslations, loadMessages, type Locale } from '~/utils/i18n'
import { createLangSlugStaticPaths } from '~/utils/langPaths'
import { resolvePageSeoMeta } from '~/utils/seoHelper'
import {
  resolveUrlLinksWithTranslation,
  slugFromContext,
} from '~/utils/urlResolver'
import { withLocalizedSettings } from '~/utils/withLocalizedSettings'

import styles from '~/styles/pages.module.css'

const MapCard = dynamic(() => import('~/components/directory/MapCard/MapCard'))

interface PageData extends PartnerCardProps {
  breadcrumb: BreadcrumbProps['list']
  allFeaturedPartners: PartnerCardProps[]
  body: {
    categories: PartnerCategories[]
    featuredPartners: PartnerCardProps[]
    name: string
    city: string
    town: string
    telephone: string
    url: string
    pageModules: any
    geoLocation: any
    summary: string
    siteUrl: string
    type: string
    slug: {
      current: string
    }
    image: ImageProps
    imageCredit?: string
  }
  pageModules: any
}

const DirectoryPartner: NextPage<PageData> = (props: PageData) => {
  const {
    body: {
      name,
      city,
      town,
      telephone,
      featuredPartners,
      pageModules,
      categories,
      geoLocation,
      siteUrl,
      summary,
      slug,
      image,
      imageCredit,
      type,
    },
    allFeaturedPartners,
  } = props

  const featuredPartnersList = (
    featuredPartners && featuredPartners.length > 0
      ? featuredPartners
      : allFeaturedPartners
  ).filter(item => item.slug && item.slug.current !== slug.current)

  const isAccredited = type === 'accredited'
  const isTrusted = type === 'trusted'

  return (
    <div>
      <HeaderCTA variant="dark" />
      <Breadcrumb list={props.breadcrumb} />
      <PartnerHeader
        title={name}
        summary={summary}
        categories={categories}
        isAccredited={isAccredited}
        isTrusted={isTrusted}
      />

      <div className={styles.directoryHeaderImage}>
        <Image objectFit="cover" asset={image.asset} alt={name} />

        {imageCredit && (
          <Text
            Tag="p"
            size={TextSizes.tertiaryTitleSml}
            className={styles.imageCredit}
          >
            {imageCredit}
          </Text>
        )}
      </div>
      {pageModules && (
        <>
          <div
            className={classNames(styles.body, styles.directoryBodyContainer)}
          >
            {pageModules?.map((block: any, i: any) => resolveBlock(block, i))}
          </div>
          <MapCard
            variant="bordered"
            partner={
              {
                name: name,
                city: city,
                town: town,
                telephone: telephone,
                siteUrl: siteUrl,
                geoLocation: geoLocation,
              } as any
            }
          />
        </>
      )}
      {featuredPartnersList && featuredPartnersList.length > 0 && (
        <FeaturedPartners
          theme="dark"
          useDivider={false}
          partners={featuredPartnersList}
        />
      )}
    </div>
  )
}

export const getStaticPropsOriginal: GetStaticProps =
  async function getStaticPropsOriginal(context) {
    try {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const { client, scorer, limiter } = await useSanity()
      const lang = (context?.params?.lang as Locale) || 'en'
      const messages = await loadMessages(lang)
      const slug = slugFromContext(context)

      // STEP 1: Pre-query to detect which body module types are actually used
      const baseSelector = createBaseSelector(
        'partner',
        [`slug.current == $slug`],
        limiter,
        scorer
      )
      const detectedTypes = await detectBodyModuleTypes(
        client,
        baseSelector,
        { slug },
        ['pageModules']
      )

      // STEP 2: Main query with only the required body modules
      const data = await client.fetch(
        `
        {
          "body": *[_type == 'partner' && slug.current == $slug ${limiter}] ${scorer} [0] {
            ...,
            meta,
            siteUrl,
            "pageModules": pageModules[]${resolveUrlLinksWithTranslation(lang)} {
              ${getBodyModules(lang, detectedTypes)}
            },
            "featuredPartners": featuredPartners[]->${partnerCard},
            "categories": category[]-> {
              "name": ${internationalizedField('name')},
              "slug": slug.current
            },
          },
          ${myPortfolioBanner}
          "allFeaturedPartners": *[_type == 'partner' && isFeatured == true] ${partnerCard}
        }`,
        { slug, lang }
      )

      const breadcrumb: BreadcrumbProps['list'] | undefined = buildBreadcrumb(
        ['the-directory', data.body.name],
        '',
        undefined,
        messages,
        lang
      )

      const breadcrumbJsonLd = breadcrumb ? getBreadcrumbJsonLd(breadcrumb) : []

      // Use centralized SEO helper with locale-aware templates
      let metaData = { ...data.body.meta }
      metaData.title = `${data?.body?.meta?.title || data?.body?.name} | MyArtBroker`

      const metaProps = { meta: metaData, _id: data._id }
      await resolvePageSeoMeta(context, metaProps, 'page')
      metaData = metaProps.meta

      return {
        props: {
          notFound: false,
          smallHeader: true,
          url: `/directory/${slugFromContext(context)}`,
          ...data,
          meta: {
            ...metaData,
          },
          breadcrumb,
          breadcrumbJsonLd,
          hasGoogleReviews: false,
          noAnimatedTheme: true,
          darkFooter: true,
          theme: 'light',
          jsonLd: {
            faqJsonLd: getFAQData(data.body?.body),
          },
        },
      }
    } catch (e) {
      console.log(e)
      return {
        notFound: true,
      }
    }
  }

// Generate paths for all partners in all languages
export const getStaticPaths = createLangSlugStaticPaths(`
  *[_type == 'partner' && defined(slug.current)] {
    "slug": slug.current
  }
`)

// Add SSR translation support
export const getStaticProps = withLocalizedSettings(async (context: any) => {
  const result = await getStaticPropsOriginal(context)
  if ('notFound' in result || 'redirect' in result) {
    return result // Return notFound or redirect directly
  }
  return { props: await addTranslations(result.props, context) }
})

export default DirectoryPartner
